import React, { useState, useEffect } from 'react';
import { useDataContext } from '../contexts/DataContext';
import { useGoogleOAuthStatus } from '../hooks/useGoogleOAuthStatus';
import { getEffectiveUserId } from '../services/accountScopedDataService';
import { currentConfig } from '../config/environment';

export default function GoogleServicesConnection() {
  const dataContext = useDataContext();
  const { selectedSources, saveSelections, refreshData } = dataContext;
  const effectiveUserId = getEffectiveUserId(); // Use account-scoped user ID
  const { 
    isConnected, 
    isLoading: isOAuthLoading, 
    error: oauthError, 
    startOAuth, 
    disconnectOAuth,
    refresh: refreshOAuthStatus 
  } = useGoogleOAuthStatus();

  // Local state for form inputs (moved to top)
  const [analyticsAccountId, setAnalyticsAccountId] = useState('');
  const [analyticsPropertyId, setAnalyticsPropertyId] = useState('');
  const [analyticsStreamId, setAnalyticsStreamId] = useState('');
  const [searchConsolePropertyUrl, setSearchConsolePropertyUrl] = useState('');
  const [placesBusinessUrl, setPlacesBusinessUrl] = useState('');

  // Connection status (moved to top)
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [connectionSuccess, setConnectionSuccess] = useState('');

  // Auto-population state
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [availableStreams, setAvailableStreams] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [isLoadingStreams, setIsLoadingStreams] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(false);

  // Load existing selections on component mount (moved to top)
  useEffect(() => {
    if (selectedSources?.analytics?.accountId) {
      setAnalyticsAccountId(selectedSources.analytics.accountId);
    }
    if (selectedSources?.analytics?.propertyId) {
      setAnalyticsPropertyId(selectedSources.analytics.propertyId);
    }
    if (selectedSources?.analytics?.streamId) {
      setAnalyticsStreamId(selectedSources.analytics.streamId);
    }
    if (selectedSources?.searchConsole?.propertyUrl) {
      setSearchConsolePropertyUrl(selectedSources.searchConsole.propertyUrl);
    }
    if (selectedSources?.places?.businessUrl) {
      setPlacesBusinessUrl(selectedSources.places.businessUrl);
    }
  }, [selectedSources]);

  // Load available accounts and sites when component mounts
  useEffect(() => {
    if (effectiveUserId && effectiveUserId !== 'default') {
      loadAvailableAccounts();
      loadAvailableSites();
    }
  }, [effectiveUserId]);

  // Load properties when account changes
  useEffect(() => {
    if (analyticsAccountId) {
      loadAvailableProperties(analyticsAccountId);
        } else {
      setAvailableProperties([]);
      setAvailableStreams([]);
    }
  }, [analyticsAccountId]);

  // Load streams when property changes
  useEffect(() => {
    if (analyticsPropertyId) {
      loadAvailableStreams(analyticsPropertyId);
    } else {
      setAvailableStreams([]);
    }
  }, [analyticsPropertyId]);

  const loadAvailableAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const response = await fetch(`${currentConfig.apiBaseUrl}/api/ga/accounts`);
        if (response.ok) {
          const data = await response.json();
          console.log('Analytics accounts:', data);
          setAvailableAccounts(data.accounts || []);
        } else {
          console.error('Failed to fetch accounts:', response.status);
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    const loadAvailableProperties = async (accountId) => {
      setIsLoadingProperties(true);
      try {
        const response = await fetch(`${currentConfig.apiBaseUrl}/api/ga/properties?accountId=${encodeURIComponent(accountId)}`);
        if (response.ok) {
          const data = await response.json();
          console.log('Analytics properties:', data);
          setAvailableProperties(data.properties || []);
        } else {
          console.error('Failed to fetch properties:', response.status);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setIsLoadingProperties(false);
      }
    };

    const loadAvailableStreams = async (propertyId) => {
      setIsLoadingStreams(true);
      try {
        const response = await fetch(`${currentConfig.apiBaseUrl}/api/ga/streams?propertyId=${encodeURIComponent(propertyId)}`);
        if (response.ok) {
          const data = await response.json();
          console.log('Analytics streams:', data);
          setAvailableStreams(data.streams || []);
          } else {
          console.error('Failed to fetch streams:', response.status);
      }
    } catch (error) {
        console.error('Error fetching streams:', error);
      } finally {
        setIsLoadingStreams(false);
      }
    };

    const loadAvailableSites = async () => {
      setIsLoadingSites(true);
      try {
        const response = await fetch(`${currentConfig.apiBaseUrl}/api/sc/sites`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSites(data.siteEntry || []);
          }
        } catch (error) {
      console.error('Failed to load sites:', error);
    } finally {
      setIsLoadingSites(false);
    }
  };
  
  // Show loading state while authentication context initializes
  if (effectiveUserId === null) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading authentication...</span>
        </div>
      </div>
    );
  }
  
  // Show message if user is not authenticated
  if (!effectiveUserId || effectiveUserId === 'default') {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-4">
            Please sign in to access Google Services configuration.
          </p>
          <p className="text-sm text-gray-500">
            You'll be redirected to the login page automatically.
          </p>
        </div>
      </div>
    );
  }





  // Handle saving service configurations
  const handleSave = async () => {
    try {
      console.log('Save function started');
      console.log('Current state values:', {
        analyticsAccountId: typeof analyticsAccountId, 
        analyticsPropertyId: typeof analyticsPropertyId,
        analyticsStreamId: typeof analyticsStreamId
      });
      
      // Check if state is corrupted
      if (typeof analyticsPropertyId === 'string' && analyticsPropertyId.includes('Configuration failed')) {
        console.error('State corruption detected! Resetting analyticsPropertyId');
        setAnalyticsPropertyId('');
        throw new Error('State corruption detected. Please try again.');
      }
      
      setIsConnecting(true);
      setConnectionError('');
      setConnectionSuccess('');

      // Validate inputs - at least one service must be configured (excluding Places)
      const hasAnalytics = analyticsAccountId && analyticsPropertyId;
      const hasSearchConsole = searchConsolePropertyUrl;
      
      if (!hasAnalytics && !hasSearchConsole) {
        throw new Error('At least one service must be configured (Analytics or Search Console)');
      }
      
      // Validate Analytics if configured
      if (hasAnalytics && (!analyticsAccountId || !analyticsPropertyId)) {
        throw new Error('Analytics Account ID and Property ID are both required when configuring Analytics');
      }
      
      // Validate Search Console if configured
      if (hasSearchConsole && !searchConsolePropertyUrl) {
        throw new Error('Search Console Property URL is required when configuring Search Console');
      }



      // Update selected sources
      const newSelections = {
        analytics: {
          accountId: analyticsAccountId,
          propertyId: analyticsPropertyId,
          streamId: analyticsStreamId
        },
        searchConsole: {
          propertyUrl: searchConsolePropertyUrl
        },
        places: {
          businessUrl: placesBusinessUrl
        }
      };

      await saveSelections(newSelections);

      setConnectionSuccess('Configuration saved successfully! You can now connect to Google services.');
      
    } catch (error) {
      console.error('Save error:', error);
      setConnectionError(`Configuration failed: ${error.message}`);
    } finally {
      setIsConnecting(false);
    }
  };



  // Handle disconnecting services
  const handleDisconnect = async (serviceType) => {
    try {
      setConnectionError('');
      setConnectionSuccess('');

      if (serviceType === 'analytics') {
        setAnalyticsAccountId('');
        setAnalyticsPropertyId('');
        setAnalyticsStreamId('');
        await saveSelections({
          ...selectedSources,
          analytics: { accountId: '', propertyId: '', streamId: '' }
        });
      } else if (serviceType === 'searchConsole') {
        setSearchConsolePropertyUrl('');
        await saveSelections({
          ...selectedSources,
          searchConsole: { propertyUrl: '' }
        });
      } else if (serviceType === 'places') {
        setPlacesBusinessUrl('');
        await saveSelections({
          ...selectedSources,
          places: { businessUrl: '' }
        });
      }

      setConnectionSuccess(`${serviceType} disconnected successfully`);
      
    } catch (error) {
      console.error('Disconnect error:', error);
      setConnectionError(`Failed to disconnect ${serviceType}: ${error.message}`);
    }
  };

  // Refresh data for a specific service
  const handleRefreshData = async (serviceType) => {
    try {
      setConnectionError('');
      setConnectionSuccess('');
      
      if (effectiveUserId) {
        await refreshData(effectiveUserId, serviceType);
        setConnectionSuccess(`${serviceType} data refreshed successfully`);
      }
    } catch (error) {
      console.error('Refresh error:', error);
      setConnectionError(`Failed to refresh ${serviceType} data: ${error.message}`);
    }
  };

  // Check if any service is configured (use current form values, not saved state)
  const hasAnyService = (analyticsAccountId && analyticsPropertyId) || 
                       searchConsolePropertyUrl;

  return (
      <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Google Services Connection</h2>
      
      {/* OAuth Connection Section */}
      <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">🔐 Google Account Connection</h3>
        
        {isOAuthLoading ? (
          <div className="flex items-center space-x-2 mb-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-blue-800">Checking connection status...</span>
            </div>
        ) : isConnected ? (
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-3">
              <span className="text-green-600 text-lg">✅</span>
              <span className="text-green-800 font-medium">Connected to Google</span>
            </div>
            <p className="text-green-700 mb-3">
              Your Google account is connected and ready to use. You can now configure services below.
            </p>
            <button
              onClick={disconnectOAuth}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              🔌 Disconnect Google Account
            </button>
          </div>
        ) : (
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-3">
              <span className="text-red-600 text-lg">❌</span>
              <span className="text-red-800 font-medium">Not Connected to Google</span>
            </div>
            <p className="text-red-700 mb-3">
              You need to connect your Google account to access Analytics, Search Console, and other services.
            </p>
            <button
              onClick={startOAuth}
              disabled={isConnecting}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {isConnecting ? 'Connecting...' : '🔗 Connect Google Account'}
            </button>
            <p className="text-sm text-blue-700 mt-2">
              This will redirect you to Google to authorize access to your data.
            </p>
          </div>
        )}
        
        {oauthError && (
          <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-md">
            <p className="text-red-800 text-sm">
              <strong>Error:</strong> {oauthError}
            </p>
          <button
              onClick={refreshOAuthStatus}
              className="mt-2 text-red-700 underline text-sm hover:text-red-900"
          >
              Try again
          </button>
          </div>
        )}
      </div>

      {/* Service Configuration */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">⚙️ Service Configuration</h3>
        
            <div className="space-y-4">
            {/* Google Analytics Configuration - Horizontal Layout */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Analytics Configuration
                </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Analytics Account */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Account
                  </label>
                  {isLoadingAccounts ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                      <span className="text-xs text-gray-500">Loading...</span>
                    </div>
                  ) : availableAccounts.length > 0 ? (
                              <select 
                      value={analyticsAccountId}
                      onChange={(e) => setAnalyticsAccountId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">Select Account</option>
                      {availableAccounts.map((account) => (
                  <option key={account.name} value={account.name}>
                    {account.displayName || account.name}
                  </option>
                ))}
              </select>
                  ) : (
                    <input
                      type="text"
                      value={analyticsAccountId}
                      onChange={(e) => setAnalyticsAccountId(e.target.value)}
                      placeholder="Account ID"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  )}
              </div>

                {/* Analytics Property */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Property
                  </label>
                  {analyticsAccountId && isLoadingProperties ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                      <span className="text-xs text-gray-500">Loading...</span>
                    </div>
                  ) : analyticsAccountId && availableProperties.length > 0 ? (
                  <select 
                      value={analyticsPropertyId}
                      onChange={(e) => setAnalyticsPropertyId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">Select Property</option>
                      {availableProperties.map((property) => (
                      <option key={property.name} value={property.name}>
                        {property.displayName || property.name}
                      </option>
                    ))}
                  </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        value={analyticsPropertyId}
                        onChange={(e) => setAnalyticsPropertyId(e.target.value)}
                        placeholder="Property ID"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        disabled={!analyticsAccountId}
                      />
                      <p className="mt-1 text-xs text-blue-600">
                        💡 <strong>How to find your Property ID:</strong> Go to Google Analytics → Admin → Property Settings. 
                        The Property ID is displayed at the top of the page (e.g., 123456789).
                      </p>
                </div>
              )}
                </div>

                {/* Analytics Stream */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Stream
                  </label>
                  {analyticsPropertyId && isLoadingStreams ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                      <span className="text-xs text-gray-500">Loading...</span>
                    </div>
                  ) : analyticsPropertyId && availableStreams.length > 0 ? (
                  <select 
                      value={analyticsStreamId}
                      onChange={(e) => setAnalyticsStreamId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">Select Stream</option>
                      {availableStreams.map((stream) => (
                        <option key={stream.name} value={stream.name}>
                          {stream.displayName || stream.name}
                      </option>
                    ))}
                  </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        value={analyticsStreamId}
                        onChange={(e) => setAnalyticsStreamId(e.target.value)}
                        placeholder="Stream ID"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        disabled={!analyticsPropertyId}
                      />
                      <p className="mt-1 text-xs text-blue-600">
                        💡 <strong>How to find your Stream ID:</strong> Go to Google Analytics → Admin → Data Streams. 
                        The Stream ID is displayed in the stream details (e.g., 123456789).
                      </p>
                </div>
              )}
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                💡 <strong>How to find your IDs:</strong> Go to Google Analytics → Admin → Account/Property/Data Stream Settings. 
                The IDs are displayed at the top of each page.
              </p>
            </div>

          {/* Search Console Property */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Console Property
              </label>
            {isLoadingSites ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-500">Loading sites...</span>
              </div>
            ) : availableSites.length > 0 ? (
              <select 
                value={searchConsolePropertyUrl}
                onChange={(e) => setSearchConsolePropertyUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a site</option>
                {availableSites.map((site) => (
                  <option key={site.siteUrl} value={site.siteUrl}>
                    {site.siteUrl}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={searchConsolePropertyUrl}
                onChange={(e) => setSearchConsolePropertyUrl(e.target.value)}
                placeholder="Enter Search Console Property URL"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
            <p className="mt-1 text-xs text-gray-500">
              Select your Search Console site from the dropdown, or enter the site URL manually.
            </p>
            </div>

          {/* Google Place ID - Hidden for now */}
        {/* <div className="space-y-2">
          <label htmlFor="businessUrl" className="block text-sm font-medium text-gray-700">
            Google Place ID
              </label>
          <input
            type="text"
            id="businessUrl"
            name="businessUrl"
            placeholder="e.g., 0x88d9c7e6628ae31f:0x7ac440e327175ebd"
            value={placesBusinessUrl}
            onChange={(e) => setPlacesBusinessUrl(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500">
            <strong>How to get your Place ID:</strong> Go to Google Maps, search for your business, click on it, and copy the Place ID from the URL (looks like: 0x...:0x...)
          </p>
        </div> */}
        </div>
            </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-6">
            <button
          onClick={startOAuth}
          disabled={isConnecting}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-6 rounded-lg transition-colors"
        >
          {isConnecting ? 'Connecting...' : '🔄 Re-authenticate Google Account'}
            </button>

            <button
          onClick={handleSave}
          disabled={isConnecting || !hasAnyService}
          className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-2 px-6 rounded-lg transition-colors"
        >
          {isConnecting ? 'Saving...' : '💾 Save Configuration'}
            </button>
        {!hasAnyService && (
          <p className="text-sm text-gray-500 mt-2">
            Configure at least one service to enable the save button
          </p>
          )}
        </div>

      {/* Current Status */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Current Status</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Analytics Status */}
          <div className={`p-4 rounded-lg border ${
            selectedSources?.analytics?.accountId ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Analytics</h4>
              <span className={`px-2 py-1 text-xs rounded-full ${
                selectedSources?.analytics?.accountId ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {selectedSources?.analytics?.accountId ? 'Connected' : 'Not Connected'}
              </span>
                  </div>
            {selectedSources?.analytics?.accountId && (
              <div className="text-sm text-gray-600">
                <p>Account: {selectedSources.analytics.accountId}</p>
                <p>Property: {selectedSources.analytics.propertyId}</p>
                <p>Stream: {selectedSources.analytics.streamId}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => handleRefreshData('analytics')}
                    className="text-blue-600 hover:text-blue-800 text-xs underline"
                  >
                    Refresh Data
                  </button>
                <button
                onClick={() => handleDisconnect('analytics')}
                    className="text-red-600 hover:text-red-800 text-xs underline"
              >
                Disconnect
                </button>
                </div>
              </div>
            )}
          </div>

          {/* Search Console Status */}
          <div className={`p-4 rounded-lg border ${
            selectedSources?.searchConsole?.propertyUrl ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Search Console</h4>
              <span className={`px-2 py-1 text-xs rounded-full ${
                selectedSources?.searchConsole?.propertyUrl ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {selectedSources?.searchConsole?.propertyUrl ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            {selectedSources?.searchConsole?.propertyUrl && (
              <div className="text-sm text-gray-600">
                <p className="truncate">{selectedSources.searchConsole.propertyUrl}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => handleRefreshData('searchConsole')}
                    className="text-blue-600 hover:text-blue-800 text-xs underline"
                  >
                    Refresh Data
                  </button>
              <button
                onClick={() => handleDisconnect('searchConsole')}
                    className="text-red-600 hover:text-red-800 text-xs underline"
              >
                Disconnect
              </button>
                </div>
              </div>
            )}
          </div>

          {/* Places Status - Hidden for now */}
          {/* <div className={`p-4 rounded-lg border ${
            selectedSources?.places?.businessUrl ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">Google Places</h4>
              <span className={`px-2 py-1 text-xs rounded-full ${
                selectedSources?.places?.businessUrl ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {selectedSources?.places?.businessUrl ? 'Connected' : 'Not Connected'}
              </span>
                </div>
            {selectedSources?.places?.businessUrl && (
              <div className="text-sm text-gray-600">
                <p className="truncate">{selectedSources.places.businessUrl}</p>
                <div className="flex gap-2 mt-2">
              <button
                    onClick={() => handleRefreshData('places')}
                    className="text-blue-600 hover:text-blue-800 text-xs underline"
                  >
                    Refresh Data
                  </button>
                  <button
                    onClick={() => handleDisconnect('places')}
                    className="text-red-600 hover:text-red-800 text-xs underline"
              >
                Disconnect
              </button>
                </div>
              </div>
            )}
          </div> */}
        </div>
      </div>

      {/* Error and Success Messages */}
      {connectionError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{connectionError}</p>
          </div>
      )}
      
      {connectionSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{connectionSuccess}</p>
        </div>
      )}

      {/* Information Section */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">ℹ️ How It Works</h3>
        <div className="text-sm text-gray-700 space-y-2">
          <p>• <strong>OAuth Flow:</strong> Click "Connect Google Account" to securely authorize access to your Google data</p>
          <p>• <strong>Service Configuration:</strong> Enter your Analytics Account/Property IDs, Search Console URL, and Google Place ID</p>
          <p>• <strong>Data Access:</strong> Once connected, the dashboard will automatically fetch and display your data</p>
          <p>• <strong>Security:</strong> All tokens are encrypted and stored securely in Cloudflare Workers KV</p>
        </div>
      </div>
    </div>
  );
}
